import React, { useEffect } from "react";
import { Provider } from "react-redux";

import { getSafe } from "@jslib/shared/utils/getSafe";
import { logger } from "@jslib/shared/utils/logger";

import { NewVerificationComponent } from "../NewVerificationComponent";
import { type DatabaseId, type ProgramIdAction } from "../../lib/types/types";
import { getStore } from "../../lib/VerificationService/VerificationServiceStore";
import {
  CustomDimensionNames,
  observePerformanceMetrics,
  setUserDimension,
} from "../../lib/GoogleAnalytics/ga";
import { addPredefinedHooks } from "../../lib/hooks";
import { getPublishedVersion } from "../../lib/ServerApi/ApiUrls";
import { FlagProvider } from "../FeatureFlags/FlagProvider";

interface VerificationFormProps {
  programId: DatabaseId;
  // TODO fix next line
  trackingId?: string; // eslint-disable-line
}

const store = getStore();

// This call can take place at any time after the jslib .js file is parsed
// It only needs to be called once per page-load
setUserDimension(
  CustomDimensionNames.jslibVersionActual,
  getSafe(() => getPublishedVersion(), "?"),
);

export const VerificationForm = React.forwardRef(
  ({ programId, trackingId = undefined }: VerificationFormProps, ref: any) => {
    try {
      addPredefinedHooks(); // keep this as early as possible in the jslib initialization
    } catch (e) {
      logger.warn("Failed to add predefined hooks", e);
    }

    useEffect(() => {
      // this should only happen once per form
      observePerformanceMetrics();
    });

    store.dispatch<ProgramIdAction>({ programId, type: "PROGRAM_ID" });

    if (ref) {
      // eslint-disable-next-line no-param-reassign
      ref.current = {
        refresh: React.useCallback(() => store.dispatch({ type: "FORCE_UPDATE" }), []),
        reset: React.useCallback(() => store.dispatch({ type: "RESET_STATE" }), []),
      };
    }

    return (
      <Provider store={store}>
        <FlagProvider programId={programId}>
          <NewVerificationComponent programId={programId} trackingId={trackingId} />
        </FlagProvider>
      </Provider>
    );
  },
);
